import React, { useCallback, useEffect, useState } from 'react';
import { Siren, KeyRound, Loader2, RefreshCw, ShieldAlert, Ban, Clock, CheckCircle2 } from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';

function minutesLeft(iso) {
  const m = Math.max(0, Math.round((new Date(iso) - Date.now()) / 60000));
  return m <= 0 ? 'expiring' : `${m} min left`;
}

/** Access Alerts — unusual-download alarms + live break-glass grants (revocable), scoped to the org / platform-wide for admins. */
export default function AccessAlertsPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const load = useCallback(async () => {
    try { setData(await apiCall('/api/hris/admin/access-alerts')); } catch (e) { toast.error(e.message); setData({ anomalies: [], active_grants: [], recent: [] }); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const revoke = async (g) => {
    if (!window.confirm(`Revoke ${g.actor_name || 'this user'}'s access to ${g.worker_name}'s restricted documents now?`)) return;
    setBusy(g.grant_id);
    try { await apiCall(`/api/files/access-grants/${g.grant_id}`, { method: 'DELETE' }); toast.success('Access revoked'); await load(); }
    catch (e) { toast.error(e.message); }
    setBusy('');
  };

  if (data === null) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  const th = data.thresholds || {};
  return (
    <div className="space-y-4" data-testid="access-alerts-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-slate-900 flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-amber-600" /> Document access alerts</h3>
          <p className="text-xs text-slate-500">Alarms fire after {th.downloads} worker files in {th.window_minutes} minutes. Restricted-document grants last {th.grant_ttl_minutes} minutes unless revoked here. Refreshes every 30s.</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200" data-testid="access-alerts-refresh"><RefreshCw className="w-4 h-4" /></button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <section className="bg-white border border-slate-200 rounded-xl p-4" data-testid="access-anomalies">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1.5 mb-2"><Siren className="w-3.5 h-3.5 text-red-600" /> Unusual download activity <span className="ml-auto text-slate-900">{data.anomalies.length}</span></p>
          {data.anomalies.length === 0 ? <p className="text-xs text-slate-400 italic flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> No alarms in the last 30 days.</p> : (
            <ul className="space-y-2">
              {data.anomalies.map(a => (
                <li key={a._id} className="border border-red-200 bg-red-50 rounded-lg px-3 py-2 text-xs" data-testid={`anomaly-${a._id}`}>
                  <p className="text-slate-900"><b>{a.actor_name || a.actor_id?.slice(-6)}</b> <span className="text-slate-500">({a.actor_role})</span> opened <b>{a.downloads}</b> worker files in {a.window_minutes} minutes</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">{new Date(a.created_at).toLocaleString()}</p>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="bg-white border border-slate-200 rounded-xl p-4" data-testid="access-grants">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1.5 mb-2"><KeyRound className="w-3.5 h-3.5 text-amber-600" /> Active restricted-document grants <span className="ml-auto text-slate-900">{data.active_grants.length}</span></p>
          {data.active_grants.length === 0 ? <p className="text-xs text-slate-400 italic">Nobody currently holds break-glass access.</p> : (
            <ul className="space-y-2">
              {data.active_grants.map(g => (
                <li key={g.grant_id} className="border border-amber-200 bg-amber-50 rounded-lg px-3 py-2 text-xs flex items-start gap-2" data-testid={`grant-${g.grant_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-900"><b>{g.actor_name || g.actor_id?.slice(-6)}</b> → <b>{g.worker_name}</b>{g.document_id && g.document_id !== '*' ? ' (one document)' : ' (all restricted docs)'}</p>
                    <p className="text-amber-800 italic truncate">“{g.reason}”</p>
                    <p className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1"><Clock className="w-3 h-3" /> {minutesLeft(g.expires_at)} · granted {new Date(g.created_at).toLocaleTimeString()}{g.ip_address ? ` · ${g.ip_address}` : ''}</p>
                  </div>
                  <button onClick={() => revoke(g)} disabled={busy === g.grant_id} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50" data-testid={`revoke-grant-${g.grant_id}`}>
                    {busy === g.grant_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ban className="w-3 h-3" />} Revoke
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="bg-white border border-slate-200 rounded-xl p-4" data-testid="access-recent">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Recent grant history (30 days)</p>
        {data.recent.length === 0 ? <p className="text-xs text-slate-400 italic">No restricted-document access requested yet.</p> : (
          <ul className="divide-y divide-slate-100">
            {data.recent.map(r => (
              <li key={r._id} className="py-1.5 text-xs flex items-center gap-2" data-testid={`recent-${r._id}`}>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${r.action.endsWith('revoked') ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>{r.action.endsWith('revoked') ? 'revoked' : 'granted'}</span>
                <span className="text-slate-800"><b>{r.actor_name || r.actor_id?.slice(-6)}</b> · {r.worker_name}</span>
                {r.reason && <span className="text-slate-500 italic truncate">“{r.reason}”</span>}
                <span className="ml-auto text-slate-400 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
